// Wind arrows: the sprites, and the layer that draws them.
//
// The arrows are rasterized in a 2D canvas rather than drawn by MapLibre,
// because the thing that carries speed here is LENGTH, and MapLibre's only
// data-driven lever on an icon is icon-size — which scales the whole glyph, so a
// 20 m/s arrow would come out four times as thick as a 5 m/s one and the field
// would read as weight rather than as flow. Pre-rendering one sprite per speed
// band is what keeps the shaft width fixed while the length varies.
//
// Split out of app.ts whole: this is a self-contained concern (canvas in, map
// images out) that shared nothing with the rest of the module but the map.

import { map } from './instance.ts';
import { windActive } from '../wind-state.ts';
import { ARROW_BINS, CALM_MAX, CALM_ID } from '../../lib/wind-bands.ts';
import type { ExpressionSpecification } from 'maplibre-gl';

export { ARROW_BINS, CALM_MAX };

// Length carries speed; thickness never changes.
//
// Hence a sprite per speed band rather than one sprite on a data-driven
// icon-size: icon-size scales the whole glyph, so a 20 m/s arrow would come out
// four times as THICK as a 5 m/s one and the field would read as weight rather
// than as flow. Pre-rendering the lengths is what keeps the shaft width fixed.
// Which speed gets which length is lib/wind-bands.ts.

// Hairline weight, identical in every bin. The arrows are an overlay on somebody
// else's colour field, so they have to be readable without being loud: this is
// held as thin as it can go and still survive the SDF's alpha threshold.
const SHAFT_W = 1.3;
const HEAD_W = 4.2;
const HEAD_L = 5;
// Drawn at 2x and declared as pixelRatio 2: a 1.3px shaft rasterized at 1x and
// then scaled up on a retina display is a grey smudge rather than a line.
const SPRITE_DPR = 2;

// One rasterized arrow (or the calm ring) and the pixel size it was drawn at.
interface Sprite {
  cvs: HTMLCanvasElement;
  w: number;
  h: number;
}

// A single neutral ink, not the wind palette.
//
// Colour is the field's language on this map — it is how temperature says 30°C.
// Tinting the arrows by their own ramp put two palettes in the same pixels with
// nothing to tell the reader which one a given colour belonged to. So the arrows
// give colour up entirely and keep length, which nothing else here uses.
const ARROW_INK = '220,232,247';
// ...and a dark rim around it, for the same reason the city labels have a halo:
// "the field underneath is any colour at all". A pale arrow alone is a hostage to
// what it lands on — against a cold blue it has plenty of contrast, against a
// pale yellow almost none, so the *field* would have been deciding how loud the
// wind looked. The rim fixes that from the other side: on a pale field the dark
// edge carries the arrow, on a dark field the light core does, and the pair
// straddles anything in between. It also lets the ink stay this quiet without
// vanishing.
const ARROW_RIM = '6,14,26';
const ARROW_RIM_A = 0.8;
const ARROW_RIM_W = 0.9;

// The tail fades out; the head stays solid.
//
// Half the weight of a lattice this dense is in the shafts, and the shaft is the
// part carrying the least meaning — the head says which way, the length says how
// fast, and the tail is mostly just the line joining them. Fading it drops that
// weight without shortening anything, and it points: an arrow lighter at the back
// than the front reads as coming *from* somewhere, which is exactly what wd means.
const ARROW_TAIL_ALPHA = 0.2;

// Speed also reads as prominence, not just as length.
//
// This is the "density" cue: a light wind fades most of the way into the field
// under it while a strong one stays crisp, so a windy region reads as a denser
// patch of visible arrows without a single node being removed from the lattice.
//
// Actually thinning the lattice would have been the literal reading, and it is
// wrong: an absent arrow already means "no station within 500km, so we don't
// know". Dropping calm arrows too would make emptiness mean both things at once,
// and the reader would have no way to separate still air from no data.
const ARROW_OPACITY: ExpressionSpecification = ['interpolate', ['linear'], ['get', 'ws'], 0.5, 0.22, 3, 0.38, 7, 0.62, 12, 0.78];

// One shaft-and-head silhouette, tip at -y, centred on the origin. Traced as a
// single closed path rather than a rect plus a triangle so the rim can follow the
// whole outline instead of showing up as a seam where the two would have met.
function arrowPath(ctx: CanvasRenderingContext2D, shaft: number) {
  const half = shaft / 2;
  const neck = -half + HEAD_L;
  ctx.beginPath();
  ctx.moveTo(0, -half); // tip
  ctx.lineTo(HEAD_W / 2, neck);
  ctx.lineTo(SHAFT_W / 2, neck);
  ctx.lineTo(SHAFT_W / 2, half);
  ctx.lineTo(-SHAFT_W / 2, half);
  ctx.lineTo(-SHAFT_W / 2, neck);
  ctx.lineTo(-HEAD_W / 2, neck);
  ctx.closePath();
}

// A tail-to-tip alpha ramp for one colour, in the sprite's own coordinates.
//
// The ramp is spent entirely on the shaft and reaches full strength at the neck,
// so the head is solid on every bin. Running it to the tip instead would fade the
// head itself — and worst on the short arrows, where the head is most of the mark
// and the one part that must stay crisp to be read as a direction at all.
function alongArrow(ctx: CanvasRenderingContext2D, shaft: number, rgb: string, alpha: number): CanvasGradient {
  const half = shaft / 2;
  const g = ctx.createLinearGradient(0, half, 0, -half); // tail -> tip
  const neck = Math.max(0, (shaft - HEAD_L) / shaft);
  g.addColorStop(0, `rgba(${rgb},${(alpha * ARROW_TAIL_ALPHA).toFixed(3)})`);
  g.addColorStop(neck, `rgba(${rgb},${alpha})`);
  g.addColorStop(1, `rgba(${rgb},${alpha})`);
  return g;
}

// Rim first, then fill: stroke straddles the path, so half of it lands inside and
// gets covered by the fill, leaving a clean ARROW_RIM_W edge outside.
//
// The rim fades on the same ramp as the ink rather than staying solid — it is
// there to make the ink legible, so where the ink has gone the rim is just a dark
// mark of its own, and a lattice of those is the clutter this is trying to shed.
function inkAndRim(ctx: CanvasRenderingContext2D, shaft: number) {
  ctx.lineJoin = 'round';
  ctx.lineWidth = ARROW_RIM_W * 2;
  ctx.strokeStyle = alongArrow(ctx, shaft, ARROW_RIM, ARROW_RIM_A);
  ctx.stroke();
  ctx.fillStyle = alongArrow(ctx, shaft, ARROW_INK, 1);
  ctx.fill();
}

// One arrow, pointing north, centred in its own canvas.
//
// Centred rather than tail-anchored because the node is a *sample* of a field, not
// the start of a trajectory. Tail-anchoring would push each arrow's visual mass
// half its length downwind — and since length varies by bin, the strong arrows
// would sit visibly further off their nodes than the weak ones, which would leave
// the lattice looking ragged and throw away the evenness the whole Mercator
// lattice exists to buy. Centring also makes rotation-about-the-centre correct at
// every length, short bins included.
function arrowSprite(shaft: number): Sprite {
  const pad = 2 + ARROW_RIM_W;
  const w = Math.ceil((Math.max(HEAD_W, SHAFT_W) + pad * 2) * SPRITE_DPR);
  const h = Math.ceil((shaft + pad * 2) * SPRITE_DPR);
  const cvs = document.createElement('canvas');
  cvs.width = w;
  cvs.height = h;
  const ctx = cvs.getContext('2d')!;
  ctx.scale(SPRITE_DPR, SPRITE_DPR);
  ctx.translate(w / SPRITE_DPR / 2, h / SPRITE_DPR / 2);
  arrowPath(ctx, shaft);
  inkAndRim(ctx, shaft);
  return { cvs, w, h };
}

// The calm marker: a small ring, the conventional station-plot symbol for it.
//
// No fade on this one. The ramp exists to point — lighter at the back than the
// front — and a calm ring has no back or front to tell apart; a gradient on it
// would only imply a direction the data does not have.
function calmSprite(): Sprite {
  const d = 9 + ARROW_RIM_W * 2;
  const w = Math.ceil(d * SPRITE_DPR);
  const cvs = document.createElement('canvas');
  cvs.width = w;
  cvs.height = w;
  const ctx = cvs.getContext('2d')!;
  ctx.scale(SPRITE_DPR, SPRITE_DPR);
  ctx.translate(d / 2, d / 2);
  // Rimmed like the arrows, for the same reason — it lands on the same fields.
  ctx.beginPath();
  ctx.arc(0, 0, 2.4, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(${ARROW_RIM},${ARROW_RIM_A})`;
  ctx.lineWidth = 1.2 + ARROW_RIM_W * 2;
  ctx.stroke();
  ctx.strokeStyle = `rgb(${ARROW_INK})`;
  ctx.lineWidth = 1.2;
  ctx.stroke();
  return { cvs, w, h: w };
}

// Plain RGBA icons, not SDF.
//
// SDF exists to make icon-color data-driven, and these have given colour up — it
// belongs to the field now. Paying for it would cost the rim: MapLibre derives a
// halo from the distance field, and these sprites are a hard alpha mask with no
// gradient to threshold, so icon-halo-* has nothing to work with. Baking ink and
// rim into the pixels gets a real edge instead of a fake one. icon-opacity still
// applies, which is all the density ramp needs.
export function addArrowImages() {
  const put = (id: string, { cvs, w, h }: Sprite) => {
    const data = cvs.getContext('2d')!.getImageData(0, 0, w, h).data;
    if (map.hasImage(id)) map.removeImage(id);
    map.addImage(id, { width: w, height: h, data }, { pixelRatio: SPRITE_DPR });
  };
  for (const b of ARROW_BINS) put(b.id, arrowSprite(b.shaft));
  put(CALM_ID, calmSprite());
}

// One arrow as a data URL, for the legend key. The very sprite the map draws, so a
// key entry is the thing it describes rather than a picture of it.
//
// The key shows every arrow at full strength, ignoring ARROW_OPACITY: on the map
// the fade is a second reading of speed, but in a key it would only make the short
// arrows hard to see, and the number under each one already says which is which.
export function arrowSwatch(shaft: number): { url: string; w: number; h: number } {
  const { cvs, w, h } = arrowSprite(shaft);
  return { url: cvs.toDataURL(), w: w / SPRITE_DPR, h: h / SPRITE_DPR };
}

// ['step', input, default, stop, out, ...] — the default covers everything below
// the first stop, which here is calm. Built from ARROW_BINS so the expression,
// the sprites and arrowImageFor's lookup cannot drift apart.
function arrowImageExpr(): ExpressionSpecification {
  // Built by pushing, so it is assembled as a loose array and asserted once at
  // the end — the tuple types cannot describe a half-built expression.
  const expr: unknown[] = ['step', ['get', 'ws'], CALM_ID];
  let lo = CALM_MAX;
  for (const b of ARROW_BINS) {
    expr.push(lo, b.id);
    lo = b.max;
  }
  return expr as unknown as ExpressionSpecification;
}

// The arrow field itself: an overlay that rides on top of whichever scalar field
// is selected, above the colour and below the cities.
//
// Deliberately NOT clipped to the coastline, though the colour field is. The mask
// exists because a continuous wash smeared over open water reads as fabricated
// data; an arrow is a discrete sample, and clipping the lattice would leave a
// fringe of half-arrows along every coast. Where the wind is genuinely unknown —
// beyond the 500km cutoff — the server sends no node at all, so honesty here is
// already enforced by the data rather than by geometry.
export function addWindLayer() {
  addArrowImages();
  map.addLayer({
    id: 'wind-field',
    type: 'symbol',
    source: 'wind',
    layout: {
      'icon-image': arrowImageExpr(),
      'icon-rotate': ['+', 180, ['get', 'wd']], // wd is where the wind blows FROM
      'icon-rotation-alignment': 'map',
      'icon-allow-overlap': true,
      // The lattice already guarantees the spacing. Letting MapLibre collide
      // arrows out would punch holes in a grid whose evenness is the whole point.
      'icon-ignore-placement': true,
      // Constant, not a zoom ramp: the lattice is ~44px at every zoom, so the
      // arrows must be a fixed screen size too — and a ramp here would scale
      // thickness along with length, which is exactly what the bins avoid.
      'icon-size': 1,
      // Born with the toggle's state, the way fieldLayerSpec is: the overlay is on
      // by default, and syncWind only ever has to handle changes to it.
      visibility: windActive() ? 'visible' : 'none',
    },
    // Ink and rim are baked into the sprites (see addArrowImages), so opacity is
    // the only paint left — and it is the one that carries speed.
    paint: { 'icon-opacity': ARROW_OPACITY },
  });
}
